import { useEffect, useState } from 'react';
import Alert from '@/components/common/Alert';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { isNativeApp, getPlatform } from '@/config/api';
import { countLocalPendingWork, totalLocalPendingWork } from '@/offline/local-pending-work';

/** GET /api/mobile/rollout (backend modules/maui-rollout/rollout-view.ts). */
export interface TenantRolloutView {
  stage: 'none' | 'pilot' | 'wave';
  halted: boolean;
  prompt: 'none' | 'pilot' | 'update';
  allowed_app_ids: string[];
  closed_testing_note: string | null;
}

/** The production listing every agency is on today (plan §6.3: same package). */
export const DEFAULT_ANDROID_APP_ID = 'com.cmdbridge.app.us';

const APP_ID = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

/** Where the prompt sends the user. Android only: the bridge ships on Play. Null elsewhere. */
export function storeLinks(view: TenantRolloutView, platform: string): { testing: string | null; listing: string | null } {
  if (platform !== 'android') return { testing: null, listing: null };
  const appId = view.allowed_app_ids.find((id) => APP_ID.test(id)) ?? DEFAULT_ANDROID_APP_ID;
  const id = encodeURIComponent(appId);
  return {
    testing: `https://play.google.com/apps/testing/${id}`,
    listing: `https://play.google.com/store/apps/details?id=${id}`,
  };
}

/** How often the prompt re-counts what this device still holds while it is shown. */
export const PENDING_RECHECK_MS = 15_000;

/**
 * Unsent items on this device: a number, or null while unknown (not counted
 * yet, or the count failed). Null counts as "not drained" (fail closed).
 */
function useLocalPending(enabled: boolean, count: () => Promise<number>): number | null {
  const [pending, setPending] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const run = () =>
      count().then(
        (n) => alive && setPending(Number.isFinite(n) && n >= 0 ? n : null),
        () => alive && setPending(null)
      );
    void run();
    const timer = setInterval(run, PENDING_RECHECK_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [enabled, count]);
  return pending;
}

const countAll = async (): Promise<number> => totalLocalPendingWork(await countLocalPendingWork());

/**
 * The bridge's MAUI upgrade prompt (plan P4-02 pilot / P4-03 waves). Shown only
 * in the native bridge app, only when the platform says so (pilot stage, or a
 * wave tenant whose update prompt a platform admin enabled while its M7 gate
 * was GO; never while halted). It never blocks the user and never updates
 * anything by itself. The store link is withheld while this device still holds
 * unsent work (or its count is unknown): an update must not strand a queue (MR1).
 */
export default function MauiUpgradePrompt({
  native = isNativeApp(),
  platform = getPlatform(),
  countPending = countAll,
}: {
  native?: boolean;
  platform?: string;
  /** Unsent items on this device (injectable for tests). */
  countPending?: () => Promise<number>;
}) {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useTenantQuery<TenantRolloutView>(['mobile-rollout'], '/mobile/rollout', {
    enabled: native,
    staleTime: 5 * 60_000,
  });
  const visible = native && !dismissed && !!data && !data.halted && data.prompt !== 'none';
  const pending = useLocalPending(visible, countPending);

  if (!visible || !data) return null;
  const links = storeLinks(data, platform);
  const drained = pending === 0;
  const waiting =
    pending === null ? (
      <span className="mt-1 block" data-testid="maui-upgrade-pending">
        Checking what this device still has to sync. The store link appears once everything is sent.
      </span>
    ) : (
      <span className="mt-1 block" data-testid="maui-upgrade-pending">
        {pending} item(s) on this device have not synced yet. Connect and sync them first: the store link appears once everything is sent.
      </span>
    );

  if (data.prompt === 'pilot') {
    return (
      <div className="px-3 pt-2" data-testid="maui-upgrade-prompt" data-prompt="pilot">
        <Alert variant="info" title="New Command Bridge field app: pilot" dismissible onDismiss={() => setDismissed(true)} compact>
          <span className="block">Your agency is piloting the new Command Bridge field app. Sync everything on this device before you install it.</span>
          {data.closed_testing_note && <span className="mt-1 block">{data.closed_testing_note}</span>}
          {!drained && links.testing && waiting}
          {drained && links.testing && (
            <a className="mt-2 inline-flex min-h-[44px] items-center font-medium underline" href={links.testing} target="_blank" rel="noopener noreferrer">
              Join the pilot
            </a>
          )}
        </Alert>
      </div>
    );
  }

  return (
    <div className="px-3 pt-2" data-testid="maui-upgrade-prompt" data-prompt="update">
      <Alert variant="info" title="Command Bridge update available" dismissible onDismiss={() => setDismissed(true)} compact>
        <span className="block">A new Command Bridge field app is ready for your agency. Sync everything on this device, then update from the store.</span>
        {!drained && links.listing && waiting}
        {drained && links.listing && (
          <a className="mt-2 inline-flex min-h-[44px] items-center font-medium underline" href={links.listing} target="_blank" rel="noopener noreferrer">
            Open the store
          </a>
        )}
      </Alert>
    </div>
  );
}
